import PropTypes from "prop-types";
import React from "react";
import { connect } from "react-redux";
import Select from "react-select";
import { Button, Checkbox, Message, Modal, Popup } from "semantic-ui-react";
import Table from "../../../common/components/Table/Table";
import { displayErrorMessage } from "../../../common/marvel-reducers/formMessageActions";
import * as Validation from "../../../common/utils/Validation";
import { generateCountriesSourcesFromTenantIds, getPricingDataForTenants, resetPDFPortal } from "../../../report-generation/veritas-report-gen/ReportGeneratorActions";
import { fetchOrgTenantsAndCountries } from "../actions/BillingOrganizationsActions";
import TenantsAndConfigsRow from "./TenantsAndConfigsRow";

export class AssociatedTenantsTable extends React.Component {

    static propTypes = {
        associatedTenants: PropTypes.array,
        currentOrgID: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    }

    componentDidMount() {
        Validation.clearMessages();
    }

    state = {
        selectedTenantIds: new Set(),
        comparisonTenantId: "",
        modalOpen: false,
    }

    handleToggleSelect = (tenantId, checked) => {
        this.setState(prev => {
            const updated = new Set(prev.selectedTenantIds);
            if (checked) {
                updated.add(tenantId);
            } else {
                updated.delete(tenantId);
            }
            return { selectedTenantIds: updated };
        });
    }

    handleSelectAll = (e, { checked }) => {
        const allIds = this.props.associatedTenants?.map(t => String(t.tenantid));
        this.setState({
            selectedTenantIds: checked ? new Set(allIds) : new Set()
        });
    }

    handleResetPDFReportClick = () => {
        this.props.resetPDFPortal();
    }

    handleOpenCountriesSourcesOrgModal = () => {
        this.setState({ modalOpen: true });
        this.props.fetchOrgTenantsAndCountries();
    }

    handlePricingDataReportClick = () => {
        const tenantIds = [...this.state.selectedTenantIds];
        if (tenantIds.length === 0) return;
        this.props.generatePricingDataFromTenantIds(tenantIds);
    }

    handleCloseCountriesSourcesOrgModal = () => {
        this.setState({ modalOpen: false, comparisonTenantId: "" });
    }

    handleModalSubmit = () => {
        const tenantIds = [...this.state.selectedTenantIds];
        if (tenantIds.length === 0) return;
        const comparisonTenantId = this.state.comparisonTenantId ?? "";
        this.props.generateCountriesSourcesFromTenantIds(tenantIds, comparisonTenantId);
        this.setState({ modalOpen: false });
    }

    handleComparisonTenantIdChange = (option) => {
        this.setState({ comparisonTenantId: option?.value ?? "" });
    }

    get tableHeaders() {
        const allIds = this.props.associatedTenants?.map(t => String(t.tenantid)) ?? [];
        const allSelected = allIds.length > 0 && allIds.every(id => this.state.selectedTenantIds.has(id));
        const someSelected = allIds.some(id => this.state.selectedTenantIds.has(id));
        return [
            <Table.Header key="select">
                <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected && !allSelected}
                    onChange={this.handleSelectAll}
                />
            </Table.Header>,
            <Table.Header key="report">Generate Reports</Table.Header>,
            <Table.Header key="tenant">Tenant</Table.Header>,
            <Table.Header key="config">Config</Table.Header>
        ];
    }

    render() {
        const tenantsToShow = Array.isArray(this.props.orgTenantsCountries) ? this.props.orgTenantsCountries : [];

        const tenantOptions = tenantsToShow?.filter(org => org.orgID !== this.props.currentOrgID).flatMap(org => {
            const orgName = org.orgName || "";
            const tenants = org.Tenants || [];
            return tenants.map(t => {
                const id = t.tenantId || "";
                const name = t.tenantName || id;
                return { value: String(id), label: `${name}${orgName ? ` (${orgName})` : ""}`, raw: t, org };
            });
        });

        const submitEnabled = true; // old logic: this.state.selectedTenantIds.size > 0 && this.state.comparisonTenantId;

        const { formMessage, formMessageHeader, formErrorNotification } = this.props;

        return (
            <div className="associated-tenants-table">
                {formErrorNotification &&
                    <Message
                        error
                        header={formMessageHeader}
                        list={formMessage}
                        visible={formErrorNotification}
                        onDismiss={() => this.props.clearErrorMessage()}
                    />
                }
                <div className="tenants-config-table-header">
                    <h2>Tenants and Configs</h2>
                    <Popup
                        trigger={<Button
                            className={"reset-pdf-button"}
                            icon="repeat" size="tiny"
                            onClick={this.handleResetPDFReportClick}
                        />}
                        position='top center'
                        content="Reset PDF Portal"
                    />
                    <Popup
                        trigger={
                            <span>
                                <Button
                                    className={"generate-countries-sources-report-button"}
                                    icon="file excel outline" size="tiny"
                                    disabled={this.state.selectedTenantIds.size === 0}
                                    onClick={this.handleOpenCountriesSourcesOrgModal}
                                />
                            </span>
                        }
                        position='top center'
                        content="Generate Countries and Sources Report"
                    />
                    <Popup
                        trigger={
                            <span>
                                <Button
                                    className={"generate-pricing-data-report-button"}
                                    icon="file excel outline" size="tiny"
                                    disabled={this.state.selectedTenantIds.size === 0}
                                    onClick={this.handlePricingDataReportClick}
                                />
                            </span>
                        }
                        position='top center'
                        content="Generate Pricing Report"
                    />
                </div>
                <div className="sticky-table-container">
                    <Table
                        className="sticky-table"
                        headers={this.tableHeaders}>
                        {this.props.associatedTenants.map((tenant, idx) => (
                            <TenantsAndConfigsRow
                                key={idx}
                                tenant={tenant}
                                tenantName={tenant.tenant_name}
                                configName={tenant.config_name}
                                tenantID={tenant.tenantid}
                                configID={tenant.configid}
                                isSelected={this.state.selectedTenantIds.has(String(tenant.tenantid))}
                                onToggleSelect={this.handleToggleSelect}
                            />
                        ))}
                    </Table>
                </div>

                <Modal
                    centered
                    dimmer="blurring"
                    open={this.state.modalOpen}
                >
                    <Modal.Header>Generate Countries and Sources Report</Modal.Header>
                    <Modal.Content>
                        <p>Please select the tenant that you want to compare against the selected tenants.</p>
                        {this.props.orgTenantsCountriesLoading
                            ? <p>Loading tenants...</p>
                            : this.props.orgTenantsCountriesError
                                ? <p style={{ color: "red" }}>Failed to load tenants. Please close and try again.</p>
                                : (
                                <div>
                                    <div style={{ marginBottom: 12 }}>
                                        <Select
                                            options={tenantOptions}
                                            onChange={this.handleComparisonTenantIdChange}
                                            placeholder="Select comparison tenant"
                                            isClearable={true}
                                            isSearchable={true}
                                            value={tenantOptions.find(o => o.value === this.state.comparisonTenantId) || null}
                                            getOptionLabel={o => o.label}
                                            getOptionValue={o => o.value}
                                        />
                                    </div>
                                </div>
                            )
                        }
                    </Modal.Content>
                    <Modal.Actions>
                        <Button
                            color="red"
                            inverted
                            onClick={this.handleCloseCountriesSourcesOrgModal}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="green"
                            inverted
                            disabled={!submitEnabled}
                            onClick={this.handleModalSubmit}
                        >
                            Submit
                        </Button>
                    </Modal.Actions>
                </Modal>

            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    currentOrgID: state.billing.organizations.organizationDetail?.id,
    orgTenantsCountries: state.billing.organizations.orgTenantsCountries,
    orgTenantsCountriesLoading: state.billing.organizations.orgTenantsCountriesLoading,
    orgTenantsCountriesError: state.billing.organizations.orgTenantsCountriesError,
    formMessage: state.marvel.message.formMessage,
    formMessageHeader: state.marvel.message.formMessageHeader,
    formErrorNotification: state.marvel.message.formErrorNotification,
});

const mapDispatchToProps = dispatch => ({
    resetPDFPortal: () => { dispatch(resetPDFPortal()) },
    generateCountriesSourcesFromTenantIds: (tenantIds, comparisonTenantId) => { dispatch(generateCountriesSourcesFromTenantIds(tenantIds, comparisonTenantId)) },
    fetchOrgTenantsAndCountries: () => { dispatch(fetchOrgTenantsAndCountries()) },
    generatePricingDataFromTenantIds: (tenantIds) => { dispatch(getPricingDataForTenants(tenantIds)) },
    clearErrorMessage: () => { dispatch(displayErrorMessage([], false)) },
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(AssociatedTenantsTable)
